import type {
  RecapFilterOptionsResponse,
  RecapListQueryParams,
  RecapListResponse,
  RecapShareResponse,
  RecapEmailsResponse,
} from '@/types/recap.types';
import { useMutation, useQuery, type UseQueryOptions } from '@tanstack/react-query';
import axiosInstance from './axiosInstance';
import { toast } from '@/components/ui/use-toast';
import { downloadFilefromBuffer } from '@/lib/utils';
import type { AxiosError } from 'axios';
import type { GenericErrorResponse } from '@brainvaulttech/dgr-react-sdk';
import { useRecapStore } from '@/stores/useRecapStore';

export const getRecapListData = async (params: RecapListQueryParams): Promise<RecapListResponse> => {
  const response = await axiosInstance.get<RecapListResponse>('/recap/list', {
    params: {
      ...params,
      code: params.code?.length ? JSON.stringify(params.code) : undefined,
      recapNumber: params.recapNumber?.length ? JSON.stringify(params.recapNumber) : undefined,
    },
  });
  return response.data;
};

export const useRecapList = (params: RecapListQueryParams, enabled = true) => {
  return useQuery<RecapListResponse>({
    queryKey: ['recaps', params],
    queryFn: () => getRecapListData(params),
    staleTime: 1000 * 60 * 2,
    enabled,
  });
};

export const getRecaps = (
  params: RecapListQueryParams,
  options?: { query?: Partial<UseQueryOptions<RecapListResponse>> },
) => {
  const query = useQuery<RecapListResponse>({
    queryFn: () => getRecapListData(params),
    queryKey: ['recaps', params],
    ...options?.query,
  });

  const data = query.data?.recaps ?? [];
  const totalRows = query.data?.totalRows ?? 0;

  return {
    data,
    totalRows,
    isFetching: query.isFetching,
  };
};

export const getRecapFilterOptions = async () => {
  const response = await axiosInstance.get<RecapFilterOptionsResponse>('/recap/filter-options');
  return response.data;
};

export const useRecapFilterOptions = () => {
  return useQuery<RecapFilterOptionsResponse>({
    queryKey: ['recap-filter-options'],
    queryFn: getRecapFilterOptions,
    staleTime: 1000 * 60 * 2,
  });
};

export const exportRecapZip = async (recapIds: string[]) => {
  const { exportingRecapIds, addExportingRecapIds, removeExportingRecapIds } = useRecapStore.getState();
  if (recapIds.some((id) => exportingRecapIds.includes(id))) {
    return;
  }

  addExportingRecapIds(recapIds);
  toast({
    description: 'Preparing recap export...',
    duration: 3000,
  });
  try {
    const response = await axiosInstance.post<Blob>(
      '/recap/export',
      { recapIds },
      { responseType: 'blob', timeout: 10 * 60 * 1000 },
    );
    const fileName = recapIds.length === 1 ? 'recap_export.zip' : 'recaps_export.zip';
    downloadFilefromBuffer(response.data, fileName, 'ZIP');
  } catch {
    toast({
      description: 'Unable to export recap(s), please try again',
      duration: 3000,
      variant: 'destructive',
    });
  } finally {
    removeExportingRecapIds(recapIds);
  }
};

export const getRecapEmails = async (recapIds: string[]): Promise<RecapEmailsResponse> => {
  const response = await axiosInstance.get<RecapEmailsResponse>(`/recap/emails`, { params: { recapIds } });
  return response.data;
};

export const useShareRecapMutation = () => {
  return useMutation<RecapShareResponse, AxiosError<GenericErrorResponse>, { recapIds: string[] }>({
    mutationFn: async ({ recapIds }) => {
      const response = await axiosInstance.post<RecapShareResponse>('/recap/share', { recapIds });
      return response.data;
    },
  });
};
